import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
	faArrowRight,
	faChevronLeft,
	faChevronRight,
	faFire,
	faGrid2,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type {
	PortalCategoryTileDto,
	PortalStorefrontItemCardDto,
} from '@supply-mover-2-0/shared';
import type { JSX, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../providers/TranslationProvider';
import { ResilientImage } from '../ResilientImage';
import { ItemCarousel, ItemCarouselSkeleton } from './ItemCarousel';

/**
 * The storefront home page's "Shop by category" and "Popular right now" rows,
 * as presentational components: the caller loads the data and handles
 * navigation. Shared so the storefront and the CRM page builder draw the same
 * rows from the same components (PD-16588).
 */

type RowDensity = 'standard' | 'compact';

function RowHeader({
	icon,
	title,
	linkLabel,
	onLink,
}: {
	icon: IconDefinition;
	title: string;
	linkLabel: string;
	onLink?: () => void;
}): JSX.Element {
	return (
		<header className="flex items-center justify-between mb-3">
			<h2 className="flex items-center gap-2 text-base md:text-lg font-semibold text-default">
				<FontAwesomeIcon icon={icon} className="text-portal-accent" />
				{title}
			</h2>
			<button
				type="button"
				onClick={onLink}
				className="text-xs md:text-sm text-portal-accent hover:opacity-80 inline-flex items-center gap-1"
			>
				{linkLabel}
				<FontAwesomeIcon icon={faArrowRight} className="text-[10px]" />
			</button>
		</header>
	);
}

function RowMessage({ message }: { message: string }): JSX.Element {
	return (
		<div className="flex-1 flex items-center justify-center text-center px-4 py-6">
			<p className="text-sm text-brand-muted max-w-md">{message}</p>
		</div>
	);
}

/**
 * Horizontal row of category cards (picture and name). Categories without an
 * uploaded picture show the name on a gradient, so the row never has holes.
 * Hidden entirely when there are no categories to show.
 */
export function CategoryTilesRow({
	title,
	categories,
	isLoading,
	isError,
	density = 'standard',
	onSelectCategory,
	onBrowseAll,
	onLikelyStaleUrl,
}: {
	title?: string;
	categories: Array<PortalCategoryTileDto>;
	isLoading: boolean;
	isError: boolean;
	density?: RowDensity;
	onSelectCategory?: (name: string) => void;
	onBrowseAll?: () => void;
	onLikelyStaleUrl?: () => void;
}): JSX.Element | null {
	const { t } = useTranslation();
	const scrollerRef = useRef<HTMLDivElement | null>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	// A category without a usable name would link to an unfiltered catalog,
	// which looks like a broken filter, so it is skipped.
	const visible = categories.filter((cat) => cat.name.trim() !== '');

	const updateArrows = useCallback(() => {
		const el = scrollerRef.current;
		if (el == null) {
			return;
		}
		setCanScrollLeft(el.scrollLeft > 4);
		setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
	}, []);

	useEffect(() => {
		updateArrows();
		const el = scrollerRef.current;
		if (el == null) {
			return;
		}
		el.addEventListener('scroll', updateArrows, { passive: true });
		window.addEventListener('resize', updateArrows);
		return (): void => {
			el.removeEventListener('scroll', updateArrows);
			window.removeEventListener('resize', updateArrows);
		};
	}, [updateArrows, visible.length]);

	// Must stay below every hook: returning earlier breaks the rules of hooks
	// when the row re-renders into the empty state, which blanks the page.
	if (!isLoading && !isError && visible.length === 0) {
		return null;
	}

	const scrollByViewport = (direction: 1 | -1): void => {
		const el = scrollerRef.current;
		if (el == null) {
			return;
		}
		el.scrollBy({
			left: el.clientWidth * 0.85 * direction,
			behavior: 'smooth',
		});
	};

	return (
		<section className="w-full h-full flex flex-col">
			<RowHeader
				icon={faGrid2}
				title={t(title ?? 'Shop by category')}
				linkLabel={t('Browse all')}
				onLink={onBrowseAll}
			/>
			{isLoading ? (
				<ItemCarouselSkeleton />
			) : isError ? (
				<RowMessage message={t("We couldn't load categories right now.")} />
			) : (
				<div className="relative flex-1">
					<div
						ref={scrollerRef}
						className="flex gap-3 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-1 px-1"
						style={{ scrollbarWidth: 'thin' }}
					>
						{visible.map((cat) => (
							<button
								key={cat.name}
								type="button"
								onClick={() => onSelectCategory?.(cat.name.trim())}
								className={`snap-start shrink-0 ${
									density === 'compact'
										? 'w-[80px] sm:w-[110px] md:w-[160px]'
										: 'w-[112px] sm:w-[140px] md:w-[160px]'
								} text-left bg-brand-surface border border-brand-border rounded-lg overflow-hidden hover:shadow-md transition-shadow`}
							>
								<div className="aspect-square bg-brand-input flex items-center justify-center relative overflow-hidden">
									<ResilientImage
										src={cat.imageUrl}
										alt={cat.displayName}
										className="w-full h-full object-cover"
										loading="lazy"
										onLikelyStaleUrl={onLikelyStaleUrl}
										fallback={
											<div
												className="w-full h-full flex items-center justify-center p-3 text-center"
												style={{
													background:
														'linear-gradient(135deg, #fbbf24, #f59e0b)',
												}}
											>
												<span className="text-white font-bold text-sm md:text-base drop-shadow line-clamp-3">
													{t(cat.displayName)}
												</span>
											</div>
										}
									/>
								</div>
								<div className="p-2">
									<p className="text-sm font-semibold text-default line-clamp-1">
										{t(cat.displayName)}
									</p>
									<p className="text-[11px] text-brand-muted">
										{t('{{count}} items', { count: cat.itemCount })}
									</p>
								</div>
							</button>
						))}
					</div>

					{canScrollLeft && (
						<button
							type="button"
							onClick={() => {
								scrollByViewport(-1);
							}}
							aria-label={t('Scroll left')}
							className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-8 h-8 items-center justify-center rounded-full bg-surface shadow-lg border border-brand-border hover:bg-brand-input z-10"
						>
							<FontAwesomeIcon icon={faChevronLeft} />
						</button>
					)}
					{canScrollRight && (
						<button
							type="button"
							onClick={() => {
								scrollByViewport(1);
							}}
							aria-label={t('Scroll right')}
							className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-8 h-8 items-center justify-center rounded-full bg-surface shadow-lg border border-brand-border hover:bg-brand-input z-10"
						>
							<FontAwesomeIcon icon={faChevronRight} />
						</button>
					)}
				</div>
			)}
		</section>
	);
}

/**
 * "Popular right now": the brand's most widely bought items, as a scrolling
 * row of item cards. Hidden entirely when there is nothing to show.
 */
export function PopularItemsRow({
	title,
	items,
	isLoading,
	isError,
	density,
	showPrices,
	onBrowseAll,
	onSelectItem,
	renderCardActions,
	onLikelyStaleUrl,
}: {
	title?: string;
	items: Array<PortalStorefrontItemCardDto>;
	isLoading: boolean;
	isError: boolean;
	density?: RowDensity;
	showPrices: boolean;
	onBrowseAll?: () => void;
	onSelectItem?: (item: PortalStorefrontItemCardDto) => void;
	renderCardActions?: (item: PortalStorefrontItemCardDto) => ReactNode;
	onLikelyStaleUrl?: () => void;
}): JSX.Element | null {
	const { t } = useTranslation();

	if (!isLoading && !isError && items.length === 0) {
		return null;
	}

	return (
		<section className="w-full h-full flex flex-col">
			<RowHeader
				icon={faFire}
				title={t(title ?? 'Popular right now')}
				linkLabel={t('Browse catalog')}
				onLink={onBrowseAll}
			/>
			{isLoading ? (
				<ItemCarouselSkeleton />
			) : isError ? (
				<RowMessage message={t("We couldn't load popular items right now.")} />
			) : (
				<ItemCarousel
					items={items}
					density={density}
					showPrices={showPrices}
					onSelectItem={onSelectItem}
					renderCardActions={renderCardActions}
					onLikelyStaleUrl={onLikelyStaleUrl}
					emptyState={
						<RowMessage
							message={t(
								'No popular items to show yet — check back as the catalog gets more activity.',
							)}
						/>
					}
				/>
			)}
		</section>
	);
}
